import React, { useState, useEffect, useRef, useCallback } from 'react';

// Optimized gallery images - WebP format, max 800px width, ~50KB each
// Shuffled for variety
const galleryImages = [
  { url: "/gallery/22_workshop_discussion.webp", alt: "Workshop Discussion" },
  { url: "/opt/photo/img-2225.webp", alt: "Executive Roundtable Meeting" },
  { url: "/opt/photo/img-0143.webp", alt: "Leadership Training Session" },
  { url: "/gallery/10_coach_session.webp", alt: "Leadership Coach Session", position: "top" },
  { url: "/opt/photo/dfo.webp", alt: "RESILIGENT Team Workshop" },
  { url: "/opt/photo/img-0065.webp", alt: "Executive Development Programme" },
  { url: "/opt/photo/img-2222.webp", alt: "Leadership Strategy Session" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-35-04.webp", alt: "Executive Boardroom Presentation" },
  { url: "/opt/photo/20241010-101203.webp", alt: "Team Leadership Discussion" },
  { url: "/opt/photo/img-1637.webp", alt: "Corporate Leadership Training" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-28-56-1-2.webp", alt: "Corporate Leadership Training" },
  { url: "/gallery/35_cbn_leadership.webp", alt: "CBN Leadership Programme" },
  { url: "/opt/photo/20241010-103652.webp", alt: "Team Collaboration Session" },
  { url: "/gallery/08_facilitator.webp", alt: "RESILIGENT Facilitator", position: "center 35%" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-25-25.webp", alt: "Leadership Programme Graduation", position: "top" },
  { url: "/opt/photo/20241010-102238.webp", alt: "Executive Team Meeting" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-28-57-3.webp", alt: "Executive Leadership Session" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-31-06.webp", alt: "Polaris Bank Leadership Training", position: "top" },
  { url: "/opt/photo/img-6990.webp", alt: "BaxterStorey Leadership Workshop" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-25-25-1.webp", alt: "Leadership Development Workshop", position: "top" },
  { url: "/gallery/41_corporate_governance.webp", alt: "Corporate Governance Programme" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-28-57-4.webp", alt: "RESILIGENT DISC Training Session" },
  { url: "/gallery/04_boi_boardroom.webp", alt: "Bank of Industry Boardroom" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-25-25-2.webp", alt: "Team Leadership Celebration" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-29-37.webp", alt: "Leadership Retreat Session" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-28-56.webp", alt: "Leadership Conference Event" },
  { url: "/gallery/14_cultural_nuances.webp", alt: "Cultural Nuances Workshop" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-25-25-3.webp", alt: "Executive Training Session" },
  { url: "/gallery/40_workshop_session.webp", alt: "Workshop Session" },
  { url: "/gallery/06_exec_board.webp", alt: "Executive Board Presentation" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-28-56-1.webp", alt: "Nigerian Breweries Strategy Presentation" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-28-57.webp", alt: "Change Management Training" },
  { url: "/gallery/24_talent_management.webp", alt: "Talent Management Training" },
  { url: "/gallery/03_thor_explorations.webp", alt: "Thor Explorations Leadership" },
  { url: "/opt/photo/whatsapp-image-2026-01-08-at-16-28-57-1.webp", alt: "Leadership Facilitation Workshop" },
  { url: "/gallery/18_workshop_presentation.webp", alt: "Workshop Presentation" },
  { url: "/gallery/37_management_dev.webp", alt: "Management Development Programme" },
  { url: "/gallery/15_whiteboard.webp", alt: "Whiteboard Exercise" },
  { url: "/gallery/09_polaris_manager.webp", alt: "Polaris Bank Manager's Development" },
  { url: "/gallery/43_training_facilitation.webp", alt: "Training Facilitation", position: "center 35%" },
  { url: "/gallery/20_leadership_facilitation.webp", alt: "Leadership Facilitation", position: "center 10%" },
  { url: "/gallery/16_nigerian_workplace.webp", alt: "Nigerian Workplace Training", position: "center 25%" },
  { url: "/gallery/32_workshop_audience.webp", alt: "Leadership Workshop Audience" },
];

// Optimized Image component with Intersection Observer for true lazy loading
const GalleryImage = ({ image, index, imageNumber, isMobile }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Reset state when image changes
    setIsLoaded(false);
    setHasError(false);
    setIsInView(false);
  }, [image.url]);

  // Generate optimized image URL with size hints for external images
  const getOptimizedUrl = (url, width) => {
    // For external URLs, we can't resize, but we hint the browser
    return url;
  };

  return (
    <div
      ref={containerRef}
      className="relative aspect-[4/3] rounded-xl overflow-hidden group animate-fadeIn bg-slate-800/50 backdrop-blur-sm"
    >
      {/* Placeholder skeleton with shimmer effect */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 animate-shimmer" />
      )}
      
      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
          <div className="text-center">
            <svg className="w-8 h-8 mx-auto mb-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-slate-500 text-xs">Image unavailable</span>
          </div>
        </div>
      )}
      
      {/* Only load image when in view */}
      {isInView && (
        <img
          ref={imgRef}
          src={getOptimizedUrl(image.url, isMobile ? 400 : 600)}
          alt={image.alt}
          loading="lazy"
          decoding="async"
          fetchPriority={index === 0 ? "high" : "low"}
          width={isMobile ? "400" : "600"}
          height={isMobile ? "300" : "450"}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className={`w-full h-full object-cover transition-all duration-500 ease-out ${
            isMobile ? '' : 'group-hover:scale-105'
          } ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={image.position ? { objectPosition: image.position } : {}}
        />
      )}
      
      {/* Gradient overlay - always visible on mobile for better UX */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent ${
        isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      } transition-opacity duration-300`} />
      
      {/* Caption - always visible on mobile */}
      <div className={`absolute bottom-0 left-0 right-0 p-3 ${
        isMobile ? 'translate-y-0' : 'translate-y-full group-hover:translate-y-0'
      } transition-transform duration-300`}>
        <p className="text-white text-xs sm:text-sm font-medium line-clamp-2">
          {image.alt}
        </p>
      </div>
    </div>
  );
};

const GallerySection = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const galleryRef = useRef(null);
  
  // Minimum swipe distance for navigation (in px)
  const minSwipeDistance = 50;
  
  // Detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Responsive images per page: 1 on mobile, 2 on tablet, 3 on desktop
  const imagesPerPage = isMobile ? 1 : isTablet ? 2 : 3;
  const totalPages = Math.ceil(galleryImages.length / imagesPerPage);

  // Auto-advance with pause on interaction
  const [isPaused, setIsPaused] = useState(false);
  
  useEffect(() => {
    if (isPaused) return;
    
    const timer = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }, isMobile ? 3000 : 4000); // Faster on mobile
    
    return () => clearInterval(timer);
  }, [totalPages, isPaused, isMobile]);

  // Reset page when switching between screen sizes
  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(0);
    }
  }, [isMobile, isTablet, currentPage, totalPages]);

  const currentImages = galleryImages.slice(
    currentPage * imagesPerPage,
    (currentPage + 1) * imagesPerPage
  );

  // Preload next page images (only on non-mobile for performance)
  useEffect(() => {
    if (isMobile) return; // Skip preloading on mobile to save bandwidth
    
    const nextPage = (currentPage + 1) % totalPages;
    const nextImages = galleryImages.slice(
      nextPage * imagesPerPage,
      (nextPage + 1) * imagesPerPage
    );
    
    nextImages.forEach(image => {
      const img = new Image();
      img.src = image.url;
    });
  }, [currentPage, imagesPerPage, totalPages, isMobile]);

  // Touch handlers for swipe navigation
  const onTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsPaused(true);
  }, []);

  const onTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      setIsPaused(false);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    } else if (isRightSwipe) {
      setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    }
    
    // Resume auto-advance after 3 seconds
    setTimeout(() => setIsPaused(false), 3000);
  }, [touchStart, touchEnd, totalPages]);

  // Navigation functions
  const goToPage = useCallback((page) => {
    setCurrentPage(page);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 3000);
  }, []);

  const goNext = useCallback(() => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 3000);
  }, [totalPages]);

  const goPrev = useCallback(() => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 3000);
  }, [totalPages]);

  return (
    <section className="relative bg-[#0a1628] py-10 sm:py-12 lg:py-16 overflow-hidden">
      {/* Subtle pattern overlay - matching Core Domains exactly */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="galleryGrid" width="10" height="10" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.5" fill="#00A6DA" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#galleryGrid)" />
        </svg>
      </div>
      
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-6 sm:mb-8 lg:mb-10">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-6 sm:w-8 h-px bg-gradient-to-r from-transparent to-[#2693d0]/50" />
            <span className="text-[#2693d0] text-xs sm:text-sm font-semibold uppercase tracking-wider">Portfolio</span>
            <div className="w-6 sm:w-8 h-px bg-gradient-to-l from-transparent to-[#2693d0]/50" />
          </div>
          <h2 className="text-white font-semibold text-xl sm:text-2xl lg:text-4xl mb-2 sm:mb-3">
            Our Work In Action
          </h2>
          <p className="text-slate-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto px-2">
            Leadership development, coaching, and organisational transformation across Africa and beyond.
          </p>
        </div>

        {/* Gallery Container with Touch Support */}
        <div 
          ref={galleryRef}
          className="relative"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Navigation Arrows - Hidden on mobile (use swipe instead) */}
          {!isMobile && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 lg:-translate-x-4 z-10 w-10 h-10 lg:w-12 lg:h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 border border-white/10"
                aria-label="Previous images"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 lg:translate-x-4 z-10 w-10 h-10 lg:w-12 lg:h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 border border-white/10"
                aria-label="Next images"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Gallery Grid - Responsive columns */}
          <div className={`grid gap-3 sm:gap-4 lg:gap-5 mb-4 sm:mb-6 px-0 sm:px-8 lg:px-14 ${
            isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-2' : 'grid-cols-3'
          }`}>
            {currentImages.map((image, index) => (
              <GalleryImage 
                key={`${currentPage}-${index}`} 
                image={image} 
                index={index}
                imageNumber={currentPage * imagesPerPage + index + 1}
                isMobile={isMobile}
              />
            ))}
          </div>

          {/* Swipe hint for mobile */}
          {isMobile && (
            <p className="text-slate-500 text-xs text-center mb-3 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              Swipe to browse
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </p>
          )}
        </div>

        {/* Pagination Dots - Larger touch targets on mobile */}
        <div className="flex justify-center items-center gap-1.5 sm:gap-2 flex-wrap max-w-xs sm:max-w-md mx-auto">
          {Array.from({ length: Math.min(totalPages, 20) }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToPage(index)}
              className={`rounded-full transition-all duration-300 ${
                index === currentPage 
                  ? 'bg-[#2693d0] w-6 sm:w-8 h-2.5 sm:h-2' 
                  : 'bg-white/30 hover:bg-white/50 w-2.5 sm:w-2 h-2.5 sm:h-2'
              }`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
          {totalPages > 20 && (
            <span className="text-slate-500 text-xs ml-2">+{totalPages - 20} more</span>
          )}
        </div>

        {/* Page counter for mobile */}
        {isMobile && (
          <p className="text-slate-400 text-xs text-center mt-3">
            {currentPage + 1} / {totalPages}
          </p>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default GallerySection;
